import { useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Badge, Loader } from '@lms/ui';
import { PERMISSIONS } from '@lms/shared-types';
import { usePermissions } from '@/hooks/usePermissions';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { api } from '@/lib/api';
import { hierarchyLevelLabel, isSystemRole, permissionLabel, roleHierarchyLevel, roleLabel } from '@/lib/role-display';
import { studentProfilePath } from '@/lib/user-detail-navigation';

interface RoleUser {
  id: string;
  name: string;
  fullName?: string;
  email?: string;
  status?: string;
  avatarUrl?: string | null;
}

interface RoleDetail {
  id: string;
  name: string;
  label?: string | null;
  hierarchyLevel?: number;
  permissions?: string[];
  usersCount?: number;
  users?: RoleUser[];
  _count?: { users?: number };
}

const PANEL = 'rounded-xl border border-dash-outline-variant bg-white p-6 shadow-sm md:p-8';
const SECTION_HEADING = 'flex items-center gap-3 border-b border-dash-outline-variant pb-4';
const USERS_PER_PAGE = 10;
const USER_STATUS_LABELS: Record<string, string> = { active: 'Activo', inactive: 'Inactivo' };

function initials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p.charAt(0).toUpperCase())
    .join('');
}

export function RoleShowPage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const profileFrom = `${location.pathname}${location.search}`;
  const { canManageRole, hasPermission } = usePermissions();
  const canEditRoles = hasPermission(PERMISSIONS.ROLES_EDITAR);
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(1);

  const { data, isLoading, error } = useQuery({
    queryKey: ['roles', id],
    queryFn: () => api<RoleDetail>(`/roles/${id}`),
    enabled: Boolean(id),
  });

  const label = roleLabel(data?.name ?? '', data?.label);
  const permissionCount = data?.permissions?.length ?? 0;
  const users = data?.users ?? [];
  const lastPage = Math.max(1, Math.ceil(users.length / USERS_PER_PAGE));
  const currentPage = Math.min(page, lastPage);
  const pageUsers = users.slice((currentPage - 1) * USERS_PER_PAGE, currentPage * USERS_PER_PAGE);
  const rangeStart = users.length === 0 ? 0 : (currentPage - 1) * USERS_PER_PAGE + 1;
  const rangeEnd = Math.min(currentPage * USERS_PER_PAGE, users.length);
  const userCount = data?.usersCount ?? data?._count?.users ?? users.length;
  const system = data ? isSystemRole(data.name) : false;
  const level = data?.hierarchyLevel ?? (data ? roleHierarchyLevel(data.name) : null);
  const manageable = data ? canEditRoles && canManageRole(data.name, data.hierarchyLevel) : false;

  const permissionTags = useMemo(() => {
    return (data?.permissions ?? [])
      .map((perm) => ({ key: perm, label: permissionLabel(perm) }))
      .sort((a, b) => a.label.localeCompare(b.label, 'es'));
  }, [data?.permissions]);

  const filteredTags = useMemo(() => {
    const term = filter.trim().toLowerCase();
    if (!term) return permissionTags;
    return permissionTags.filter((tag) => tag.label.toLowerCase().includes(term));
  }, [permissionTags, filter]);

  const assignUsersUrl = `/usuarios?role=${encodeURIComponent(data?.name ?? '')}`;

  return (
    <div className="lms-site-admin-hub-page font-sans">
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-start gap-4">
          <HubBackLink to="/roles" label="Volver a roles" />
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">{label}</h1>
              <span className="rounded-full bg-brand-100 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-dash-primary">
                {data?.name}
              </span>
              {level != null && (
                <span className="rounded-full bg-dash-primary/10 px-3 py-1 text-xs font-bold text-dash-primary">
                  {hierarchyLevelLabel(level)}
                </span>
              )}
            </div>
            <p className="mt-1 max-w-2xl text-dash-on-surface-variant">
              Configuración y permisos globales del rol
            </p>
          </div>
        </div>
        {manageable && (
          <Link
            to={`/roles/${id}/edit`}
            className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-dash-primary px-6 py-2.5 text-sm font-medium text-white shadow-sm transition-all hover:opacity-90 active:scale-[0.98]"
          >
            <span className="material-symbols-outlined text-lg" aria-hidden="true">
              edit
            </span>
            Editar rol
          </Link>
        )}
      </div>

      {error && (
        <p className="mb-4 text-sm text-error-600" role="alert">
          {(error as Error).message}
        </p>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <section className={`${PANEL} flex h-fit flex-col gap-8 lg:col-span-1`}>
            <div className={SECTION_HEADING}>
              <span className="material-symbols-outlined text-dash-secondary" aria-hidden="true">
                info
              </span>
              <h2 className="text-xl font-semibold text-dash-on-surface">Resumen</h2>
            </div>
            <div className="space-y-6">
              <div className="flex flex-col gap-1">
                <span className="text-xs font-semibold uppercase tracking-tight text-dash-on-surface-variant">Nombre</span>
                <span className="text-lg text-dash-on-surface">{label}</span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-xs font-semibold uppercase tracking-tight text-dash-on-surface-variant">Identificador</span>
                <span className="w-fit rounded bg-secondary-100 px-2 py-0.5 font-mono text-sm text-dash-on-surface-variant">{data?.name}</span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-xs font-semibold uppercase tracking-tight text-dash-on-surface-variant">Tipo</span>
                <span className="text-base text-dash-on-surface">{system ? 'Rol del sistema' : 'Personalizado'}</span>
              </div>
              <div className="mt-2 grid grid-cols-2 gap-4">
                <div className="flex flex-col items-center justify-center rounded-lg border border-secondary-200 bg-secondary-100/50 p-4">
                  <span className="text-4xl font-bold text-dash-secondary">{permissionCount}</span>
                  <span className="text-xs font-semibold text-secondary-900">Permisos</span>
                </div>
                <div className="flex flex-col items-center justify-center rounded-lg border border-dash-outline-variant bg-secondary-50 p-4">
                  <span className="text-4xl font-bold text-dash-on-surface-variant">{userCount}</span>
                  <span className="text-xs font-semibold text-dash-on-surface-variant">Usuarios</span>
                </div>
              </div>
            </div>
          </section>

          <section className={`${PANEL} flex flex-col gap-6 lg:col-span-2`}>
            <div className="flex flex-col gap-4 border-b border-dash-outline-variant pb-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <span className="material-symbols-outlined text-dash-secondary" aria-hidden="true">
                  verified_user
                </span>
                <h2 className="text-xl font-semibold text-dash-on-surface">Permisos asignados</h2>
              </div>
              <div className="relative w-full sm:w-auto">
                <span className="material-symbols-outlined pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xl text-dash-on-surface-variant" aria-hidden="true">
                  search
                </span>
                <input
                  type="search"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder="Filtrar permisos..."
                  className="w-full rounded-full border border-dash-outline-variant py-2 pl-10 pr-4 text-sm outline-none transition-all focus:border-dash-secondary focus:ring-2 focus:ring-dash-secondary/20 sm:w-64"
                />
              </div>
            </div>

            {permissionTags.length === 0 ? (
              <p className="text-sm text-dash-on-surface-variant">Este rol no tiene permisos asignados.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {filteredTags.map((tag) => (
                  <span
                    key={tag.key}
                    className="cursor-default rounded-full border border-dash-outline-variant bg-dash-surface px-3 py-1.5 text-xs font-medium text-dash-on-surface-variant transition-all hover:-translate-y-px hover:border-brand-200 hover:bg-brand-50 hover:text-dash-primary"
                  >
                    {tag.label}
                  </span>
                ))}
              </div>
            )}
          </section>

          {users.length === 0 ? (
            <section className={`${PANEL} lg:col-span-3 flex flex-col border-dashed`}>
              <div className={`${SECTION_HEADING} mb-2 w-full`}>
                <span className="material-symbols-outlined text-dash-secondary" aria-hidden="true">
                  group
                </span>
                <h2 className="text-xl font-semibold text-dash-on-surface">Usuarios con este rol</h2>
              </div>
              <div className="flex min-h-[16rem] flex-col items-center justify-center py-10 text-center">
                <div className="mb-6 flex h-24 w-24 items-center justify-center rounded-full border border-dash-outline-variant bg-secondary-50">
                  <span className="material-symbols-outlined text-5xl text-dash-on-surface-variant" aria-hidden="true">
                    person_off
                  </span>
                </div>
                <h3 className="text-xl font-semibold text-dash-on-surface">Sin usuarios</h3>
                <p className="mx-auto mt-2 max-w-md text-sm text-dash-on-surface-variant">
                  Ningún usuario tiene asignado este rol actualmente. Puede asignar este rol desde el módulo de gestión de usuarios.
                </p>
                <Link
                  to={assignUsersUrl}
                  className="mt-8 inline-flex items-center justify-center rounded-lg border border-dash-secondary px-6 py-2 text-sm font-medium text-dash-secondary transition-colors hover:bg-secondary-50"
                >
                  Asignar usuarios
                </Link>
              </div>
            </section>
          ) : (
            <section className="lms-gradebook lms-gradebook--forum lg:col-span-3">
              <div className="lms-course-forum-card">
                <div className="lms-course-forum-card__head">
                  <div className="lms-course-forum-card__heading">
                    <h2 className="lms-course-forum-card__title">Usuarios con este rol</h2>
                  </div>
                </div>

                <div className="lms-gradebook__table-wrap">
                  <table className="lms-gradebook__table">
                    <thead>
                      <tr>
                        <th className="lms-gradebook__th" scope="col">
                          Nombre completo
                        </th>
                        <th className="lms-gradebook__th" scope="col">
                          Dirección de correo
                        </th>
                        <th className="lms-gradebook__th" scope="col">
                          Estado
                        </th>
                        <th className="lms-gradebook__th lms-gradebook__th--actions" scope="col">
                          Acciones
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {pageUsers.map((user) => {
                        const displayName = user.fullName ?? user.name;
                        const profileTo = studentProfilePath(user.id, profileFrom);
                        return (
                          <tr key={user.id}>
                            <th className="lms-gradebook__td" scope="row">
                              <div className="lms-gradebook__student">
                                {user.avatarUrl ? (
                                  <img src={user.avatarUrl} alt="" className="lms-gradebook__avatar" />
                                ) : (
                                  <div className="lms-gradebook__avatar" aria-hidden="true">
                                    {initials(displayName)}
                                  </div>
                                )}
                                <Link to={profileTo} className="lms-gradebook__name">
                                  {displayName}
                                </Link>
                              </div>
                            </th>
                            <td className="lms-gradebook__td">
                              {user.email ? (
                                <a href={`mailto:${user.email}`} className="lms-gradebook__mail">
                                  {user.email}
                                </a>
                              ) : (
                                <span className="lms-gradebook__muted">—</span>
                              )}
                            </td>
                            <td className="lms-gradebook__td">
                              <Badge variant={user.status === 'active' || !user.status ? 'success' : 'default'}>
                                {USER_STATUS_LABELS[user.status ?? 'active'] ?? user.status}
                              </Badge>
                            </td>
                            <td className="lms-gradebook__td lms-gradebook__td--actions">
                              <Link
                                to={profileTo}
                                className="lms-gradebook__action"
                                title="Ver usuario"
                                aria-label={`Ver usuario ${displayName}`}
                              >
                                <span className="material-symbols-outlined" aria-hidden="true">
                                  visibility
                                </span>
                              </Link>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="lms-course-forum-card__footer">
                  <span className="lms-course-forum-card__meta">
                    Mostrando <strong>{rangeStart}</strong> a <strong>{rangeEnd}</strong> de{' '}
                    <strong>{users.length}</strong> resultados
                  </span>
                  <div className="lms-course-forum-card__pager">
                    <button
                      type="button"
                      className="lms-course-forum-card__pager-btn"
                      aria-label="Página anterior"
                      disabled={currentPage <= 1}
                      onClick={() => setPage(currentPage - 1)}
                    >
                      <span className="material-symbols-outlined" aria-hidden="true">
                        chevron_left
                      </span>
                    </button>
                    <span className="lms-course-forum-card__pager-current">{currentPage}</span>
                    <button
                      type="button"
                      className="lms-course-forum-card__pager-btn"
                      aria-label="Página siguiente"
                      disabled={currentPage >= lastPage}
                      onClick={() => setPage(currentPage + 1)}
                    >
                      <span className="material-symbols-outlined" aria-hidden="true">
                        chevron_right
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
